import {AfterViewInit, Component, DestroyRef, ElementRef, HostListener, inject, OnInit, OnDestroy} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import {Location} from '@angular/common';
import {Title} from '@angular/platform-browser';
import {select, Store} from '@ngrx/store';
import {currentPlanDetailSelector} from '../../../store';
import {map, take} from 'rxjs/operators';
import {Actions, ofType} from '@ngrx/effects';
import {
  AppActionTypes,
  GetNewHireClasses,
  TaskCancel,
  TaskComplete,
  TaskError,
  ApplyNewHireClasses,
  DiscardNewHireClasses
} from '../../../store/actions';
import {MessageService, ScrollerOptions} from 'primeng/api';
import {NgbModal} from '@ng-bootstrap/ng-bootstrap';
import {CanComponentDeactivate} from '../../../helpers/can-deactivate-guard.service';
import {ActivatedRouteSnapshot, RouterStateSnapshot} from '@angular/router';
import {DialogOptions} from '../../../common/dialog-options';
import {WfmModalComponent as modalComponent} from '../../../components/wfm-modal/wfm-modal.component';
import {AuthenticationService} from '../../../authentication.service';
import {TenantService} from '../../../tenant.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {LearningCurveDialogComponent} from './learning-curve-dialog/learning-curve-dialog.component';
import {NewHireClassDialogComponent} from './new-hire-class-dialog/new-hire-class-dialog.component';
import { ColDef, GridOptions } from 'ag-grid-community';
import { suppressGridTabToExit, enableGridControlActivation, enableFullWidthRowKeyboardNav, enableGridInteractiveControlTabOrder, associateGridRowHeaders, fixEmptyFullWidthRowGroup, canTakeGridFocus } from '../../../common/utils/ag-grid-a11y';
import { associateMultiselectVisibleLabel, sanitizeMultiselectComboboxValue } from '../../../common/utils/multiselect-a11y';
import { DateUtils } from '../../../common/utils/date';
import { format, parseISO } from 'date-fns';
import { Observable } from 'rxjs';
import { RowGroupingModule } from '@ag-grid-enterprise/row-grouping';

import { ModuleRegistry } from '@ag-grid-community/core';
import {EspNoRowsOverlayComponent} from "../../../components/no-rows-overlay/no-rows-overlay.component";
import { NewHireLearningCurvesResponse, NewHireSettings } from 'src/app/models/new-hire';
import { LearningCurveGroupRendererComponent } from 'src/app/components/learning-curve-group-renderer/learning-curve-group-renderer.component';
import { ClipboardModule } from '@ag-grid-enterprise/clipboard';
import { RangeSelectionModule } from '@ag-grid-enterprise/range-selection';
import { EnterpriseCoreModule } from '@ag-grid-enterprise/core';
import { EspTableHeaderComponent } from 'src/app/components/esp-table-header/esp-table-header.component';
import {PlanLoadingOverlayComponent} from "../plan-loading-overlay/plan-loading-overlay.component";
import {environment} from "../../../../environments/environment";
import { NewHireMuLinkCellRendererComponent } from './new-hire-mu-link-cell-renderer.component';
import { CachedMU, LRUCache, PlanDetailResponseV2, SelectedMU } from 'src/app/models/plan';
import { GridItemActionComponent } from 'src/app/components/grid-item-action/grid-item-action.component';

@Component({
  standalone: false,
  selector: 'esp-new-hire',
  providers: [MessageService,TranslateService],
  templateUrl: './new-hire.component.html',
  styleUrl: './new-hire.component.scss'
})
export class NewHireComponent implements OnInit, OnDestroy, AfterViewInit, CanComponentDeactivate {
  planName: string;
  planDetails: any;
  task: any;
  _isLoading = true;
  _isLoadingError = false;
  _isGridValid = true;
  _isPristine = true;
  private _cameFromMUView = false;
  planDetailsSaved = false;
  selectedMUs = [];
  managementUnitsList: any;
  private shouldRefreshMUs: boolean = false;
  selectedLearningCurve: any = null;
  EspNoRowsOverlayComponent = EspNoRowsOverlayComponent;
  gridApi: any;
  PlanLoadingOverlayComponent = PlanLoadingOverlayComponent;
  isReadOnly: boolean = false;
  maxPlanNewHireWeeks: number = 26;
  virtualScrollOptions: ScrollerOptions = {
    autoSize: true
  }
  protected contactTypes: { oid: string; id: string; name: string; label: string }[] = [];

  // Locale handling
  public dateLocale = DateUtils.getDatefnsLocale("en");
  public language = "en";
  public locale = "en-US";
  public username = null;
  public groupRowRendererParams: any;
  public userInfo$: Observable<string>;
  public newHireSettings: NewHireSettings;

  public modules = [ClipboardModule, RangeSelectionModule, EnterpriseCoreModule, RowGroupingModule]

  // Grid properties
  colDefs: ColDef[];
  isLoadingLanguage: boolean = true;
  frameworkComponents: any = {
    agColumnHeader: EspTableHeaderComponent,
    newHireMuLinkCellRenderer: NewHireMuLinkCellRendererComponent,
    gridItemActionComponent: GridItemActionComponent
  }
  icons:any = {
    checkboxDisabled: `<svg class="icon"><use xlink:href="#icon-rect-disabled" /></svg>`,
    checkboxCheckedReadOnly: `<svg class="icon"><use xlink:href="#icon-rect-readonly" /></svg>`,
    sortAscending: `<svg class="icon"><use xlink:href="#icon-sort-ascending" /></svg>`,
    sortDescending: '<svg class="icon"><use xlink:href="#icon-sort-descending" /></svg>'
  }

  planSettings: any;

  formatDate(value: any): string {
    if (value) {
      // Use parseISO to handle ISO date strings without timezone issues
      const date = typeof value === 'string' ? parseISO(value) : new Date(value);
      const localeConfig = environment.locales.find(loc => loc.locale === this.locale);
      // Default format is the English locale from the environment.ts file
      const dateFormat = localeConfig?.dateFormat || 'M/d/yyyy'; // Default format if locale not found
      return format(date, dateFormat, { locale: this.dateLocale });
    }
    return '';
  }

  gridOptions: GridOptions = {
    defaultColDef: {
      flex: 1,
      width: 100,
      // WCAG 1.4.4 Resize text / 1.4.10 Reflow: give every column a readable
      // minimum width so headers and data cells are never squeezed down to a
      // single truncated character, and let users drag-resize columns to reveal
      // any remaining hidden content.
      minWidth: 120,
      resizable: true,
      // a11y (WAI-ARIA "grid" pattern / WCAG 2.1.1 Keyboard, 2.4.3 Focus Order):
      // Tab / Shift+Tab visit only the grid's INTERACTIVE controls (sort,
      // expand, Add Class, row link, Delete, week controls), in true document
      // order and symmetrically in both directions; plain read-only cells are
      // never Tab stops and are reached with the Arrow keys instead, which
      // also activate the interactive controls with Enter / Space.
      //
      // The authoritative implementation is the grid-host-level
      // `enableGridInteractiveControlTabOrder` listener wired in `onGridReady`
      // — it intercepts Tab in the capture phase before AG Grid's own Tab
      // handling runs, which is required because that handling never lets Tab
      // reach a full-width row's nested controls, and applies asymmetrically
      // for a custom header's nested control (ESP-18550). `suppressGridTabToExit`
      // listener does not intercept, and — crucially — for Tab pressed while a
      // cell editor is OPEN. `enableGridInteractiveControlTabOrder` bails out on
      // `.ag-cell-inline-editing`, so without running here during editing AG Grid
      // would cycle Tab cell-to-cell and never exit (a keyboard trap, WCAG 2.1.2:
      // "focus was stuck when pressing Tab"). suppressGridTabToExit acts only on
      // Tab, moving focus out of the grid (blurring/committing the editor); every
      // other key is left to the editor.
      suppressKeyboardEvent: (params) => suppressGridTabToExit(params),
      // Header cells: same fallback as above — the authoritative handling is
      // `enableGridInteractiveControlTabOrder`.
      suppressHeaderKeyboardEvent: suppressGridTabToExit,
      cellStyle: {
        textAlign: 'left'
      }
    },
    // a11y (WCAG 4.1.2 Name, Role, Value): these grids do NOT support row
    // selection, so `rowSelection` is intentionally left unset. Enabling it
    // made AG Grid mark rows aria-selected and announce "Selected. Press space
    // to deselect this row" on non-selectable cells, which is misleading.
    suppressNoRowsOverlay: true,
    stopEditingWhenCellsLoseFocus: true,
    enableBrowserTooltips: true,
    // a11y (ESP-21718 family) / ESP-21806: columns are not draggable at all,
    // so there is no column-reorder DOM/Tab-order desync to fix and no need
    // for the grid-wide `ensureDomOrder` option (whose forced body-row DOM
    // re-splicing on scroll recycle caused stale hover / duplicate tooltips
    // during fast scrolling on the plan list grid, ESP-21806).
    suppressMovableColumns: true,
    //groupDisplayType: 'groupRows',
    groupDefaultExpanded: 1,
    isFullWidthRow:this.isFullWidthRow.bind(this),
    fullWidthCellRenderer:LearningCurveGroupRendererComponent,
    treeData: true,
    getDataPath: (data: any) => data.dataPath || [],
    autoGroupColumnDef: {
      colId: 'managementUnit',
      // a11y (WCAG 1.3.1 Info and Relationships): the tree/group column holds
      // the row label (Management Unit), so mark its cells as row headers and
      // screen readers announce the correct row header for each data cell.
      cellAriaRole: 'rowheader',
      headerValueGetter: (params) => this.translate.instant('new.hire.management.unit'),
      cellRenderer: 'newHireMuLinkCellRenderer',
      cellRendererParams: {
        suppressCount: true
      },
      minWidth: 250,
      flex: 2,
      valueGetter: this.getValueForRow.bind(this),
      tooltipValueGetter: this.getValueForRow.bind(this),
      sortable: true,
      comparator: (valueA: string, valueB: string, nodeA: any, nodeB: any) => {
        const dataA = nodeA?.data;
        const dataB = nodeB?.data;


        if (!dataA || !dataB) {
          return (valueA || '').toLowerCase().localeCompare((valueB || '').toLowerCase());
        }

        if (dataA.type === 'class' && dataB.type === 'class') {
          const idA = dataA.muId ?? null;
          const idB = dataB.muId ?? null;

          // Compare by ID first
          if (idA !== null && idB !== null) {
            if (idA !== idB) {
              return idA - idB;
            }
          } else if (idA !== null) {
            return -1;
          } else if (idB !== null) {
            return 1;
          }

          // IDs are equal or both don't have IDs, compare by managementUnit case-insensitively
          const nameA = (dataA.managementUnit || '').toLowerCase();
          const nameB = (dataB.managementUnit || '').toLowerCase();
          return nameA.localeCompare(nameB);
        }

        // For curve rows, sort by name case-insensitively
        if (dataA.type === 'curve' && dataB.type === 'curve') {
          const nameA = (dataA.name || '').toLowerCase();
          const nameB = (dataB.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        }

        return (valueA || '').toLowerCase().localeCompare((valueB || '').toLowerCase());
      },
      cellStyle: {
        textAlign: 'left'
      }
    },
    context: {
      onDeleteClassRow: this.onDeleteClassRow.bind(this),
      onEditLearningCurve: this.onEditLearningCurve.bind(this),
      onEditClass: this.showEditClassDialog.bind(this),
      isLoading:()=>this._isLoading,
      onAddClass: this.showAddClassDialog.bind(this)
    },
    rowClassRules: {
      "readonly-row":(params)=>true
    }
  };

  rowData: any[] = [];
  allRowData: any[] = [];
  private isEditingLearningCurve: boolean = false;
  modifiedLearningCurves: Set<string> = new Set();

  private navigationState: any;

  private previousPageTitle: string | null = null;

  private readonly destroyRef = inject(DestroyRef);

  private comboboxA11yObserver: MutationObserver | null = null;
  private comboboxLabelObserver: MutationObserver | null = null;
  private rowHeaderObserver: { disconnect(): void } | null = null;
  private fullWidthRowGroupObserver: { disconnect(): void } | null = null;
  private controlActivation: { disconnect(): void } | null = null;
  private fullWidthRowNav: { disconnect(): void } | null = null;
  private gridTabOrder: { disconnect(): void } | null = null;

  constructor(private store: Store<any>,
              private action$: Actions,
              private location: Location,
              public messageService: MessageService,
              public translate: TranslateService,
              private modalService: NgbModal,
              private authService: AuthenticationService,
              public tenantService: TenantService,
              private titleService: Title,
              private el: ElementRef) {

    this.navigationState = location.getState();
    this.initHandlers();
    this.initUserInfo();

    ModuleRegistry.registerModules([RowGroupingModule]);

    this.groupRowRendererParams = {
      innerRenderer: LearningCurveGroupRendererComponent,
      suppressCount:true
    }
  }

  onLangChangeHandler() {
    this.initColumns();
    this.setPageTitle();
    if (this.gridApi) {
      this.gridApi.refreshHeader();
    }
  }

  getValueForRow(params: any): string {
    if (params.data?.type === 'curve') {
      return params.data.name;
    } else if (params.data?.type === 'class') {
      // Find the management unit by muOid and return its label
      const muItem = this.managementUnitsList?.find(mu => mu.value.oid === params.data.muOid);
      return muItem ? muItem.value.label : params.data.managementUnit;
    }
    return '';
  }

  ngOnInit() {
    this.previousPageTitle = this.titleService.getTitle();
    this.translate.onLangChange
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.onLangChangeHandler());

    // If translations are already loaded, initialize immediately; otherwise the
    // onLangChange subscription above initializes on the first language change.
    if (this.translate.currentLang) {
      this.initColumns();
      this.setPageTitle();
    }

    this.planDetailsSaved = this.navigationState?.saved;
  }

  /**
   * WCAG 2.4.2 – set a descriptive, page-specific browser title so the tab and
   * screen readers announce the current page (e.g. "New Hire Training Class -
   * NICE Enhanced Strategic Planner") instead of the generic app title.
   */
  private setPageTitle(): void {
    const page = this.translate.instant('new.hire.page.title');
    this.titleService.setTitle(this.translate.instant('browser.page.title', {page}));
  }

  ngAfterViewInit(): void {
    // WCAG 4.1.2 Name, Role, Value / 2.5.3 Label in Name (screen reader +
    // Dragon): give the Management Unit combobox a descriptive accessible name
    // that contains its visible label and selection text, and strip PrimeNG's
    // "[object Object]" value so it is announced as e.g.
    // "Management Unit, combo box, 3 selected, collapsed".
    this.comboboxA11yObserver = sanitizeMultiselectComboboxValue(this.el?.nativeElement);
    this.comboboxLabelObserver = associateMultiselectVisibleLabel(this.el?.nativeElement);
  }

  ngOnDestroy(): void {
    if (this.previousPageTitle !== null) {
      this.titleService.setTitle(this.previousPageTitle);
    }
    this.comboboxA11yObserver?.disconnect();
    this.comboboxA11yObserver = null;
    this.comboboxLabelObserver?.disconnect();
    this.comboboxLabelObserver = null;
    this.rowHeaderObserver?.disconnect();
    this.rowHeaderObserver = null;
    this.fullWidthRowGroupObserver?.disconnect();
    this.fullWidthRowGroupObserver = null;
    this.controlActivation?.disconnect();
    this.controlActivation = null;
    this.fullWidthRowNav?.disconnect();
    this.fullWidthRowNav = null;
    this.gridTabOrder?.disconnect();
    this.gridTabOrder = null;
  }

  initColumns() {
    this.colDefs = [
          {
            field: 'learningCurveName',
            headerValueGetter: (params) => this.translate.instant('new.hire.learning.curve'),
            hide: true,
            valueGetter: (params) => {
              if (params.data?.type === 'curve') {
                return params.data.name;
              } else if (params.data?.type === 'class') {
                const learningCurveOid = params.data.dataPath?.[0];
                const learningCurve = this.allRowData.find(r => r.type === 'curve' && r.learningCurveId === learningCurveOid);
                return learningCurve ? learningCurve.name : '';
              }
              return '';
            }
          },
          {
            field: 'muName',
            headerValueGetter: (params) => this.translate.instant('label.mu.short'),
            hide: true,
            valueGetter: (params) => {
              if (params.data?.type === 'class') {
                const muItem = this.managementUnitsList?.find(mu => mu.value.oid === params.data.muOid);
                return muItem ? muItem.value.label : params.data.managementUnit;
              }
              return '';
            }
          },
          {
            field: 'fteCount',
            headerValueGetter: (params) => this.translate.instant('new.hire.fte.count'),
            sortable: true,
            filter: false,
            editable: false,
            type: 'numericColumn',
            tooltipValueGetter: (params) => params.value,
            comparator: (valueA: number, valueB: number, nodeA: any, nodeB: any) => {
              const dataA = nodeA?.data;
              const dataB = nodeB?.data;

              // If either node is missing data, fallback to simple comparison
              if (!dataA || !dataB) {
                return (valueA || 0) - (valueB || 0);
              }

              // Only apply custom sorting for class rows
              if (dataA.type === 'class' && dataB.type === 'class') {
                // Primary sort: FTE count
                const fteComparison = (valueA || 0) - (valueB || 0);
                if (fteComparison !== 0) {
                  return fteComparison;
                }

                // Secondary sort: Starting Date
                const dateA = dataA.startingDate || '';
                const dateB = dataB.startingDate || '';
                const dateComparison = dateA.localeCompare(dateB);
                if (dateComparison !== 0) {
                  return dateComparison;
                }

                // Tertiary sort: MU ID
                const muIdA = dataA.muId ?? null;
                const muIdB = dataB.muId ?? null;

                if (muIdA !== null && muIdB !== null) {
                  const muIdComparison = muIdA - muIdB;
                  if (muIdComparison !== 0) {
                    return muIdComparison;
                  }
                } else if (muIdA !== null) {
                  return -1;
                } else if (muIdB !== null) {
                  return 1;
                }

                // Quaternary sort: MU name
                const muNameA = (dataA.managementUnit || '').toLowerCase();
                const muNameB = (dataB.managementUnit || '').toLowerCase();
                return muNameA.localeCompare(muNameB);
              }

              // For non-class rows or mismatched types, use simple comparison
              return (valueA || 0) - (valueB || 0);
            }
          },
          {
            field: 'startingDate',
            headerValueGetter: (params) => this.translate.instant('new.hire.starting.date'),
            sortable: true,
            sort: 'asc',
            filter: false,
            editable: false,
            type: 'dateColumn',
            width: 100,
            valueFormatter: (params) => this.formatDate(params.value),
            tooltipValueGetter: (params) => this.formatDate(params.value)
          },
          {
            field: 'endingDate',
            headerValueGetter: (params) => this.translate.instant('new.hire.ending.date'),
            sortable: true,
            width: 100,
            filter: false,
            editable: false,
            type: 'dateColumn',
            valueFormatter: (params) => this.formatDate(params.value),
            tooltipValueGetter: (params) => this.formatDate(params.value)
          },
          {
            field: 'actions',
            width: 200,
            flex: 0,
            headerName: this.translate.instant("plan.header.actions"),
            // Visually hide the Actions header label but keep it in the a11y
            // tree AND in the header Tab order (includeInHeaderTabOrder) so it
            // is a real focus stop after Ending Date. onStaticHeaderKeydown then
            // forwards focus INTO the first body control (focusFirstBodyControl)
            // on forward Tab and back to the previous sortable header on
            // Shift+Tab. Without being a stop, forward Tab from Ending Date fell
            // onto the header and exited the grid — focus appeared stuck.
            headerClass: 'ag-header-visually-hidden-label',
            headerComponentParams: {
              showTooltipOnFocus: false,
              includeInHeaderTabOrder: true,
              focusFirstBodyControl: () => this.focusFirstGridBodyControl()
            },
            sortable: false,
            filter: false,
            editable: false,
            type: 'buttonColumn',
            // ESP-21814: this grid's Tab ring is owned by the host-level
            // `enableGridInteractiveControlTabOrder`, whose walk skips .ag-cell
            // wrappers (ag-grid-a11y.ts, so plain data cells are Arrow-key
            // territory) AND anything with a negative tabindex. The Delete button
            // shipped as tabindex="-1" on the assumption that the CELL was the Tab
            // stop and Enter/Space would reach the button through
            // `enableGridControlActivation` - but under this grid's model the cell
            // is not a stop either, so the delete action had no keyboard route at
            // all (WCAG 2.1.1). `tabIndex: 0` opts this grid's button into being a
            // real Tab stop; the renderer defaults to -1 so the cell-as-stop grids
            // that share it (Manage Plans, Imported Forecasts) are unaffected.
            cellRenderer: 'gridItemActionComponent',
            cellRendererParams: {
              callback: this.onDeleteClassRow.bind(this),
              messageDelete: this.translate.instant("new.hire.class.delete.single"),
              // WCAG 4.1.2: names the button "Delete <management unit>" so the
              // control is distinguishable per row. It must NOT be "name": that
              // field exists only on the learning-curve GROUP rows, and the
              // renderer's field lookup returns early when the configured field is
              // missing, so every row's button was named just "Delete".
              itemNameField: "managementUnit",
              tabIndex: 0
            }
          }
        ];
  }

  initUserInfo() {
    this.userInfo$ = this.store.select("state");
    this.userInfo$.subscribe((value: any) => {
      if(!value) return;
      this.setUsernameLocale(value);
    });
  }

  public setUsernameLocale(value: any) {
    if (value.user) this.username = value.user.username;
    if (value.locale) this.locale = value.locale;
    if (value.user && value.user.language) {
      if (this.language != value.user.language) {
        this.dateLocale = DateUtils.getDatefnsLocale(value.user.language);
        this.language = value.user.language;
      }
    }
  }

  initHandlers() {
    this.store
      .pipe(
        select(currentPlanDetailSelector),
        take(1),
        takeUntilDestroyed()
      )
      .subscribe(this.currentPlanChanged.bind(this));

    this.action$
      .pipe(
        ofType(AppActionTypes.PlanDetailReceivedV2),
        map((detail: any) => detail.payload),
        takeUntilDestroyed()
      )
      .subscribe(this.currentPlanChanged.bind(this));

    this.action$.pipe(takeUntilDestroyed(), ofType(
      AppActionTypes.TaskCreateAction,
      AppActionTypes.TaskStartAction,
      AppActionTypes.TaskStatusAction,
      AppActionTypes.TaskErrorAction,
      AppActionTypes.TaskCancelAction,
      AppActionTypes.TaskCompleteAction)
    ).subscribe(this.taskHandler.bind(this))

    this.action$
      .pipe(
        ofType(AppActionTypes.GetNewHireClassesSuccess),
        map((action: any) => action.payload),
        takeUntilDestroyed()
      )
      .subscribe(this.getNewHireClassesSuccess.bind(this));

    this.action$
      .pipe(
        ofType(AppActionTypes.GetNewHireClassesFailed),
        map((action: any) => action.payload),
        takeUntilDestroyed()
      )
      .subscribe(this.getNewHireClassesFailed.bind(this));

    this.action$
      .pipe(
        ofType(AppActionTypes.SaveLearningCurveSuccess),
        map((action: any) => action.payload),
        takeUntilDestroyed()
      )
      .subscribe(this.saveLearningCurveSuccessHandler.bind(this));

    this.action$
      .pipe(
        ofType(AppActionTypes.SaveLearningCurveFailed),
        map((action: any) => action.payload),
        takeUntilDestroyed()
      )
      .subscribe(this.saveLearningCurveFailureHandler.bind(this));

    this.action$
      .pipe(
        ofType(AppActionTypes.ApplyNewHireClassesSuccess),
        map((action: any) => action.payload),
        takeUntilDestroyed()
      )
      .subscribe(this.applyNewHireClassesSuccessHandler.bind(this));

    this.action$
      .pipe(
        ofType(AppActionTypes.ApplyNewHireClassesFailed),
        map((action: any) => action.payload),
        takeUntilDestroyed()
      )
      .subscribe(this.applyNewHireClassesFailureHandler.bind(this));

    this.action$
      .pipe(
        ofType(AppActionTypes.DiscardNewHireClassesSuccess),
        map((action: any) => action.payload),
        takeUntilDestroyed()
      )
      .subscribe(this.discardNewHireClassesSuccessHandler.bind(this));

    this.action$
      .pipe(
        ofType(AppActionTypes.DiscardNewHireClassesFailed),
        map((action: any) => action.payload),
        takeUntilDestroyed()
      )
      .subscribe(this.discardNewHireClassesFailedHandler.bind(this));

    this.action$
      .pipe(
        ofType(AppActionTypes.DeleteLearningCurveSuccess),
        map((action: any) => action.payload),
        takeUntilDestroyed()
      ).subscribe(this.deleteLearningCurveSuccessHandler.bind(this));

    this.action$
      .pipe(
        ofType(AppActionTypes.DeleteLearningCurveFailed),
        map((action: any) => action.payload),
        takeUntilDestroyed()
      ).subscribe(this.deleteLearningCurveFailedHandler.bind(this));
  }

  currentPlanChanged(planDetails:PlanDetailResponseV2) {
    // debugger;
    if (planDetails) {
      this.planName = planDetails.planName;
      this.planDetails = planDetails;
      this.planSettings = planDetails.settings;
      this.getMuList();
      this.getCTList();
      this._isLoading = false;

      this.loadNewHireData();
    }
  }

  private taskHandler(taskAction:any) {
    // console.log("Handler: ", taskAction);
    if (taskAction instanceof TaskComplete) {
      this.task = null;
    } else if(taskAction instanceof  TaskCancel) {
      this.task = null;
    }else if(taskAction instanceof TaskError){
      this.task = null;
    } else {
      this.task = taskAction.payload;
    }
  }

  onGridReady(params: any) {
    this.gridApi = params.api;
    // The grid is ready with the mock data
    //params.api.sizeColumnsToFit();
    // a11y (WCAG 4.1.2 Name, Role, Value): AG Grid renders empty pinned/sticky
    // full-width containers as role="rowgroup" with no role="row" children,
    // which ARC flags as "rowgroup role is missing one of the row element(s)".
    // Downgrade those empty containers to role="presentation" (restored to
    // rowgroup once full-width group rows render).
    this.fullWidthRowGroupObserver?.disconnect();
    this.fullWidthRowGroupObserver = fixEmptyFullWidthRowGroup(this.el?.nativeElement);
    // a11y (WCAG 1.3.1 Info and Relationships): associate every data cell with
    // its row-header cell (the Management Unit tree column) via aria-describedby
    // so screen readers announce the correct row header as focus moves across
    // the row. The grid keeps role="treegrid" because its rows are hierarchical.
    this.rowHeaderObserver?.disconnect();
    this.rowHeaderObserver = associateGridRowHeaders(this.el?.nativeElement);
    // a11y (WCAG 2.1.1 Keyboard): under the exit model the row controls (the
    // Management Unit edit link and the Delete button) are reached with the
    // Arrow keys, not Tab. Activate the single control in the focused body cell
    // with Enter / Space. Header cells are plain sortable headers, so leave
    // them to AG Grid's native Enter-to-sort.
    this.controlActivation?.disconnect();
    this.controlActivation = enableGridControlActivation(this.el?.nativeElement, { headerCells: false });
    // a11y (WCAG 2.1.1 Keyboard / 2.4.3 Focus Order): the learning-curve group
    // row is a full-width row with THREE controls (expand/collapse toggle,
    // group-name link, Add Class button) that are real tabindex="0" Tab stops.
    // Full-width rows are not governed by defaultColDef.suppressKeyboardEvent,
    // so Up/Down Arrow stopped working the moment one of them had focus; this
    // reroutes Up/Down to the adjacent row and lets Left/Right Arrow rove
    // across the row's own controls as a bonus. Tab itself is handled by
    // `enableGridInteractiveControlTabOrder` below.
    this.fullWidthRowNav?.disconnect();
    this.fullWidthRowNav = enableFullWidthRowKeyboardNav(
      this.el?.nativeElement,
      params.api,
      '.ag-full-width-row',
      '.group-row-control'
    );
    // a11y (WCAG 2.1.1 Keyboard / 2.4.3 Focus Order): AG Grid's own Tab
    // handling never lets Tab reach a full-width row's own nested controls
    // (it only reacts when the row's container element itself has focus), so
    // the "Week controls" / Add Class buttons above were unreachable with Tab
    // (ESP-18550). This manually walks Tab/Shift+Tab to the next or previous
    // interactive control anywhere in the grid (or out of it) in true document
    // order, symmetrically in both directions, ahead of AG Grid's own handler.
    this.gridTabOrder?.disconnect();
    this.gridTabOrder = enableGridInteractiveControlTabOrder(this.el?.nativeElement, params.api);
  }

  /**
   * a11y (WCAG 2.1.1 / 2.4.3): focus the FIRST interactive control in the grid
   * body so forward Tab from the (visually hidden, non-sortable) Actions header
   * enters the grid rows instead of leaving the grid. The body's first controls
   * are the full-width group-row controls (expand toggle / group-name link /
   * Add Class), which carry the shared `.group-row-control` class; fall back to
   * any in-row Delete button. Returns true when focus was moved.
   */
  public focusFirstGridBodyControl(): boolean {
    const host = this.el?.nativeElement as HTMLElement | undefined;
    if (!host) return false;
    // Collect every candidate body control, then pick the FIRST one that can
    // actually take focus. The full-width group row renders its controls in
    // this order: expand toggle, group-name link, Add Class. For a group with
    // NO class children the toggle is `disabled` + `.invisible`, so skip
    // disabled / hidden / non-tabbable controls and land on the group-name link
    // instead (previously the disabled toggle made this report a false success
    // and focus never entered the grid when a curve had no classes).
    //
    // Order matters and must NOT be left to the DOM. `querySelectorAll` returns
    // DOCUMENT order regardless of how the selector is written, and AG Grid
    // renders `.ag-center-cols-container` (the class rows) BEFORE
    // `.ag-full-width-container` (the group rows) - so a single combined query
    // would hand back a class row's control first. That was harmless only while
    // every in-cell control was `tabindex="-1"`; ESP-21814 makes the row Delete
    // button a real Tab stop, so the first body control has to be picked
    // explicitly. The group row is the first thing the user sees, so look for its
    // controls first and only then fall back to a class row's.
    const candidates = [
      ...Array.from(host.querySelectorAll<HTMLElement>('.ag-full-width-row .group-row-control')),
      ...Array.from(host.querySelectorAll<HTMLElement>('.ag-row .group-row-control')),
      ...Array.from(host.querySelectorAll<HTMLElement>('.ag-row a[href], .ag-row button'))
    ];
    // ESP-21813: `canTakeGridFocus` is the shared predicate, so this and the
    // grid's own Tab walk agree on what "focusable" means. It also adds the
    // `visibility` check this used to be missing: Bootstrap's `.invisible` on the
    // empty group's expand toggle is `visibility: hidden`, which leaves
    // offsetParent/offsetWidth intact, so only its `[disabled]` was keeping focus
    // off it.
    const control = candidates.find((el) => canTakeGridFocus(el));
    if (!control) return false;
    control.focus();
    // Full-width-row controls: AG Grid re-homes focus onto the full-width row
    // container on the next tick, stealing it from the control we just focused,
    // so a synchronous activeElement check would wrongly report failure. Re-
    // assert focus on the next frame so it lands on the intended control (the
    // group-name link / Add Class) even for a curve row that has no classes.
    requestAnimationFrame(() => {
      const now = host.ownerDocument?.activeElement as HTMLElement | null;
      if (control.offsetParent !== null && now !== control && !control.contains(now)) {
        control.focus();
      }
    });
    return true;
  }

  async showAddLearningCurveDlg(){
    this.isEditingLearningCurve = false;
    await this.showLearningCurveDialog();
  }

  async showEditLearningCurveDlg(learningCurveData: any) {
    this.isEditingLearningCurve = true;
    await this.showLearningCurveDialog(learningCurveData);
  }

  async showAddClassDialog(learningCurveData?: any) {
    // If no learning curve is provided, try to get the selected one
    const selectedCurve = learningCurveData || this.selectedLearningCurve;

    if (!selectedCurve) {
      this.messageService.add({
        severity: "warn",
        detail: this.translate.instant("new.hire.select.learning.curve.first")
      });
      return;
    }

    try {
      const modalRef = this.modalService.open(NewHireClassDialogComponent, {
        centered: true,
        windowClass: "b-info wfm-modal",
        container: "div.new-hire-container",
        backdrop: "static",
        keyboard: false
      });

      // Pass the selected learning curve and management units to the dialog
      modalRef.componentInstance.selectedLearningCurve = selectedCurve;
      modalRef.componentInstance.managementUnits = this.managementUnitsList.map(mu => ({
        value: mu.value,
        label: mu.value.label
      }));

      // Pass the new hire settings to the dialog
      if (this.newHireSettings) {
        modalRef.componentInstance.newHireSettings = this.newHireSettings;
      }

      // Pass the plan settings to the dialog
      if (this.planSettings) {
        modalRef.componentInstance.planSettings = this.planSettings;
      }

      const result = await modalRef.result;

      if (result) {
        // Add new class to the grid
        this.addNewClass(result, selectedCurve);
      }
    } catch(e) {
      // Dialog was dismissed
    }
  }

  async showEditClassDialog(classData: any) {
    // Find the learning curve for this class
    const learningCurveOid = classData.dataPath[0];
    const learningCurve = this.rowData.find(row => row.learningCurveId === learningCurveOid);

    if (!learningCurve) {
      this.messageService.add({
        severity: "error",
        detail: this.translate.instant("new.hire.learning.curve.not.found")
      });
      return;
    }

    try {
      const modalRef = this.modalService.open(NewHireClassDialogComponent, {
        centered: true,
        windowClass: "b-info wfm-modal",
        container: "div.new-hire-container",
        backdrop: "static",
        keyboard: false
      });

      // Pass the learning curve to the dialog
      modalRef.componentInstance.selectedLearningCurve = learningCurve;

      // Set edit mode and pass existing class data
      modalRef.componentInstance.isEditMode = true;
      modalRef.componentInstance.existingClassData = classData;

      // Pass the new hire settings to the dialog
      if (this.newHireSettings) {
        modalRef.componentInstance.newHireSettings = this.newHireSettings;
      }

      // Pass the plan settings to the dialog
      if (this.planSettings) {
        modalRef.componentInstance.planSettings = this.planSettings;
      }

      const result = await modalRef.result;

      if (result) {
        // Update the existing class in the grid
        this.updateExistingClass(classData, result, learningCurve);
      }
    } catch(e) {
      // Dialog was dismissed
    }
  }

  private async showLearningCurveDialog(existingLearningCurve?: any) {
    try {
      const modalRef = this.modalService.open(LearningCurveDialogComponent, {
        centered: true,
        windowClass: "b-info wfm-modal",
        container: "div.new-hire-container",
        backdrop: "static",
        keyboard: false
      });

      // Pass data to the dialog
      modalRef.componentInstance.contactTypesInput.set(this.contactTypes);
      modalRef.componentInstance.planId = this.planDetails.planId;
      modalRef.componentInstance.maxTrainingWeeks = this.maxPlanNewHireWeeks;
      if (this.newHireSettings?.startDayOfWeek) {
        modalRef.componentInstance.weekStartDay = this.newHireSettings.startDayOfWeek;
      }

      // Set edit mode and pass existing data if provided
      if (existingLearningCurve) {
        modalRef.componentInstance.isEditMode = true;
        modalRef.componentInstance.existingLearningCurve = existingLearningCurve;
        modalRef.componentInstance.hasClasses = this.hasClassesForLearningCurve(existingLearningCurve.learningCurveId);
        // this.isEditingLearningCurve = true; // Already set in showEditLearningCurveDlg
      } else {
        // this.isEditingLearningCurve = false; // Already set in showAddLearningCurveDlg
      }

      const result = await modalRef.result;

      if (result) {
        if (result.delete && existingLearningCurve) {
          this.deleteLearningCurve(existingLearningCurve.learningCurveId);
        } else if (existingLearningCurve) {
          // Update existing learning curve
          this.updateExistingLearningCurve(existingLearningCurve, result);
        } else {
          // Add new learning curve
          this.addNewLearningCurve(result);
        }
      }
    } catch(e) {
      // Dialog was dismissed
    }
  }

  deleteLearningCurve(learningCurveId: string) {
    // Remove the learning curve
    this.allRowData = this.allRowData.filter(row => row.learningCurveId !== learningCurveId);

    // Remove all classes associated with this learning curve
    this.allRowData = this.allRowData.filter(row => !(row.type === 'class' && row.dataPath && row.dataPath[0] === learningCurveId));

    this.modifiedLearningCurves.delete(learningCurveId);
    this._isPristine = false;
    this.filterGridBySelectedMUs();
  }

  private updateExistingLearningCurve(learningCurveData: any, result: any) {
    // Update the existing learning curve in the allRowData
    const index = this.allRowData.findIndex(row => row.learningCurveId === learningCurveData.learningCurveId);
    if (index !== -1) {
      this.allRowData[index] = {
        ...this.allRowData[index],
        name: result.name,
        trainingWeeks: result.trainingWeeks,
        trainingWeeksData: result.trainingWeeksData
      };

      this.modifiedLearningCurves.add(learningCurveData.learningCurveId);
      this._isPristine = false;
      // Apply filtering to update visible data
      this.filterGridBySelectedMUs();
    }
  }

  private addNewLearningCurve(result: any) {
    const newLearningCurve = {
      type: 'curve',
      learningCurveId: result.learningCurveId,
      name: result.name,
      trainingWeeks: result.trainingWeeks,
      cts: result.cts,
      trainingWeeksData: result.trainingWeeksData,
      dataPath: [result.learningCurveId]
    };

    this.allRowData = [...this.allRowData, newLearningCurve];
    this._isPristine = false;
    this.modifiedLearningCurves.add(result.learningCurveId);

    // Apply filtering to update visible data
    this.filterGridBySelectedMUs();
  }

  private addNewClass(result: any, learningCurveData: any) {
    const newClasses = result.managementUnits.map(mu => {
      const uuid = crypto.randomUUID(); // Generate a unique ID for each class
      return {
        type: 'class',
        oid: uuid,
        muOid: mu.oid,
        managementUnit: mu.label,
        fteCount: result.fteCount,
        startingDate: result.startDate,
        endingDate: result.endDate,
        dataPath: [learningCurveData.learningCurveId, uuid]
      };
    });

    this.allRowData = [...this.allRowData, ...newClasses];
    this.modifiedLearningCurves.add(learningCurveData.learningCurveId);
    this._isPristine = false;
    // Apply filtering to update visible data
    this.filterGridBySelectedMUs();
  }

  private updateExistingClass(classData: any, result: any, learningCurveData: any) {
    // Find the index of the class to update in allRowData
    const index = this.allRowData.findIndex(row => row.oid === classData.oid);

    if (index !== -1) {
      // Update the existing class with new data
      this.allRowData[index] = {
        ...this.allRowData[index],
        fteCount: result.fteCount,
        startingDate: result.startDate,
        endingDate: result.endDate
      };

      this.modifiedLearningCurves.add(learningCurveData.learningCurveId);
      this._isPristine = false;
      // Apply filtering to update visible data
      this.filterGridBySelectedMUs();
    }
  }

  updateSelectedMUs() {
    this.managementUnitsList.forEach((mu: any) => {
      mu.isSelected = this.selectedMUs.indexOf(mu.value) != -1;
    });

    // Set flag to filter when panel is closed instead of immediately
    this.shouldRefreshMUs = true;

    // Sync back to localStorage if user came from MU view
    if (this._cameFromMUView) {
      this.syncMUSelectionToCache();
    }
  }

  filterGridBySelectedMUs() {
    if (this.selectedMUs.length === 0) {
      // If no MUs are selected, show no data
      this.rowData = [];
    } else {
      // Get the OIDs of selected MUs
      const selectedMUOids = this.selectedMUs.map(mu => mu.oid);

      // Filter to show only:
      // - Learning curves (always show)
      // - Classes that belong to selected MUs
      this.rowData = this.allRowData.filter(row => {
        if (row.type === 'curve') {
          return true; // Always show learning curves
        } else if (row.type === 'class') {
          return selectedMUOids.includes(row.muOid); // Show classes only if their MU is selected
        }
        return false;
      });
    }

    if (this.gridApi) {
      this.rowData = [...this.rowData];
    }
    if (!this.rowData.length) {
      // Update grid option for no rows overlay
      this.gridOptions.suppressNoRowsOverlay = false;
      this.gridApi?.showNoRowsOverlay();
    }
  }

  muSelectorClosed() {
    console.debug("NewHireComponent: muSelectorClosed");
    if(this.shouldRefreshMUs){
      this.filterGridBySelectedMUs();
      this.shouldRefreshMUs = false;
    }
  }

  getMuList() {
    // console.log("NewHireComponent: getMuList", this.planDetails);
    // Clear previously selected MUs to prevent duplicates
    this.selectedMUs = [];

    if (!this.planDetails?.muInfo) {
      this.managementUnitsList = [];
      return;
    }

    // Get MUs from localStorage cache (from plan-detail-v5 component)
    const muOidsFromCache = this.getMUSelectionFromCache();

    this.managementUnitsList = this.planDetails.muInfo.map(mu => {
      // Check if entitySettings has a property matching mu.oid and it's not null
      const isInSettings = this.planDetails.entitySettings?.[mu.oid] !== undefined &&
        this.planDetails.entitySettings?.[mu.oid] !== null;

      // Check if this MU should be selected based on cached selection
      const isFromCache = muOidsFromCache.includes(mu.oid);

      const item = {
        value: {...mu, label: this.muItemLabel(mu)},
        isSelected: isFromCache || isInSettings
      };

      if (item.isSelected) {
        this.selectedMUs.push(item.value);
      }

      return item;
    });
  }

  getMUSelectionFromCache(): string[] {
    // Check navigation state for definitive entity view
    const navigationState = history.state;
    const entityView = navigationState?.entityView;

    // Track if user came from MU view for localStorage sync
    this._cameFromMUView = entityView === 'MU';

    // If we have definitive entity view from navigation
    if (entityView === 'CT') {
      // CT view - select all MUs
      return this.planDetails?.muInfo?.map(mu => mu.oid) || [];
    } else if (entityView === 'MU') {
      // MU view - use cached selection
      return this.getMUsFromLocalStorageCache();
    }

    // Fallback logic if no navigation state (for direct navigation or refresh)
    // In this case, we don't know the original view, so don't sync back
    this._cameFromMUView = false;
    return this.getMUsFromLocalStorageCache();
  }

  private getMUsFromLocalStorageCache(): string[] {
    const cachedMUsKey = "selectedMUs";
    const lruCacheStr = localStorage.getItem(cachedMUsKey);

    if (!lruCacheStr || !this.planDetails?.planId) {
      // If no cache, default to all MUs
      return this.planDetails?.muInfo?.map(mu => mu.oid) || [];
    }

    try {
      const lruCache: LRUCache = new LRUCache();
      lruCache.init(lruCacheStr);
      const cachedMUState: CachedMU = lruCache.get(this.planDetails.planId);

      if (!cachedMUState || !cachedMUState.selectedMUList || cachedMUState.selectedMUList.length === 0) {
        // No cached selection, default to all MUs
        return this.planDetails?.muInfo?.map(mu => mu.oid) || [];
      }

      // Return the cached selection
      return cachedMUState.selectedMUList.map((selectedMU: SelectedMU) => selectedMU.oid);
    } catch (error) {
      console.warn('Error reading MU selection from cache, defaulting to all MUs:', error);
      return this.planDetails?.muInfo?.map(mu => mu.oid) || [];
    }
  }

  private syncMUSelectionToCache(): void {
    if (!this.planDetails?.planId) {
      return;
    }

    try {
      const cachedMUsKey = "selectedMUs";
      let lruCacheStr = localStorage.getItem(cachedMUsKey);
      const lruCache: LRUCache = new LRUCache();

      if (lruCacheStr) {
        lruCache.init(lruCacheStr);
      }

      // Create SelectedMU objects from current selection
      const selectedMUList = this.selectedMUs.map((mu: any) =>
        new SelectedMU(mu.oid, mu, true) // isExpanded defaults to true
      );

      // Create CachedMU object
      const cachedMUs = new CachedMU(selectedMUList, true); // rollupExpanded defaults to true

      // Store in cache
      lruCache.set(this.planDetails.planId, cachedMUs);
      localStorage.setItem(cachedMUsKey, lruCache.asString());

      console.debug('NewHireComponent: Synced MU selection to localStorage', selectedMUList.length, 'MUs');
    } catch (error) {
      console.warn('NewHireComponent: Error syncing MU selection to localStorage:', error);
    }
  }

  muItemLabel(mu) {
    const { isCXOne } = this.tenantService;
    return isCXOne ? mu.name : `${mu.id} ${mu.name}`;
  }

  get _task() {
    return this.task;
  }

  get hasModifications(): boolean {
    return !this._isPristine;
  }

  get hasClasses(): boolean {
    return this.rowData.some(row => row.type === 'class');
  }

  private getCTList() {
    // Extract contact types from plan details
    if (this.planDetails.ctInfo) {
      this.contactTypes = this.planDetails.ctInfo.map(ct => ({
        oid: ct.oid,
        id: ct.id,
        name: ct.name,
        label: this.tenantService.isCXOne ? ct.name : `${ct.id} ${ct.name}`
      }));
    }
  }

  loadNewHireData() {
    // console.log("NewHireComponent: loadNewHireData");
    this._isLoading = true;
    this.store.dispatch(new GetNewHireClasses({planId: this.planDetails.planId}));
  }

  async showDiscardDlg() {
    const dgOption: DialogOptions = new DialogOptions();
    dgOption.titleKey = "new.hire.discard.dialog.title";
    dgOption.messageKey = "new.hire.discard.dialog.msg";
    dgOption.msgType = "warn";
    dgOption.showCancel = true;
    dgOption.confirmLabel = "btn.confirm.label";

    try {
      await modalComponent.showModalMessage(dgOption, this.modalService);
      this._isLoading = true;
      this.store.dispatch(new DiscardNewHireClasses({planId: this.planDetails.planId}));
    } catch(e) {
      //user cancelled
    }
  }

  getNewHireClassesSuccess(data: NewHireLearningCurvesResponse) {
    // console.log("NewHireComponent: getNewHireClassesSuccess", data);
    this._isLoading = false;
    this._isPristine = true;

    // Store the maxPlanNewHireWeeks from the settings
    this.maxPlanNewHireWeeks = data.settings.maxPlanNewHireWeeks;

    // Store the new hire settings for use in dialogs
    if (data.settings) {
      this.newHireSettings = data.settings;
    }

    // Transform the response into tree data for ag-grid
    this.allRowData = Object.entries(data.learningCurves)
      .sort((a, b) => a[1].name.localeCompare(b[1].name))
      .map(([learningCurveId, curve]) => {
        const parent = {
          type: 'curve',
          learningCurveId: learningCurveId, // Use the key as the oid
          name: curve.name,
          trainingWeeks: curve.trainingWeeks,
          cts: curve.cts,
          trainingWeeksData: curve.trainingWeeksData,
          dataPath: [learningCurveId],
          // Optionally, add more fields for display
        };
        const children = this.sortClasses(curve.classes.map(cls => ({
          type: 'class',
          oid: cls.oid,
          muOid: cls.muOid,
          managementUnit: this.managementUnitsList?.find(mu => mu.value.oid === cls.muOid)?.value.name || cls.muOid,
          muId: this.managementUnitsList?.find(mu => mu.value.oid === cls.muOid)?.value.id || null,
          fteCount: cls.fteCount,
          startingDate: cls.startDate,
          endingDate: cls.endDate,
          dataPath: [learningCurveId, cls.oid],
        })));
        return [parent, ...children];
      })
      .reduce((acc, val) => acc.concat(val), []);

    // Apply initial filtering based on selected MUs
    this.filterGridBySelectedMUs();
    if (!this.rowData.length) {
      // Update grid option for no rows overlay
      this.gridOptions.suppressNoRowsOverlay = false;
      this.gridApi?.showNoRowsOverlay();
    }
  }

  sortClasses(classes: any[]): any[] {
    return classes.sort((a, b) => {
      const dateComparison = a.startingDate.localeCompare(b.startingDate);
      if (dateComparison !== 0) {
        return dateComparison;
      }
      const muIdComparison = (a.muId ?? 0) - (b.muId ?? 0);
      if (muIdComparison !== 0) {
        return muIdComparison;
      }
      const managementUnitComparison = a.managementUnit.localeCompare(b.managementUnit);
      if (managementUnitComparison !== 0) {
        return managementUnitComparison;
      }
      return a.fteCount - b.fteCount;
    });
  }

  getNewHireClassesFailed(error: any) {
    console.error("NewHireComponent: getNewHireClassesFailed", error);
    this._isLoading = false;
    this._isLoadingError = true;
    this.messageService.add({severity: "error", detail: this.translate.instant("new.hire.classes.loading.error.msg")});
  }

  applyChanges() {


    // Format data according to ApplyNewHireClassesRequest schema
    const newHireLearningList = Array.from(this.modifiedLearningCurves).map(learningCurveOid => {
      // Find the learning curve data
      const learningCurve = this.allRowData.find(row => row.type === 'curve' && row.learningCurveId === learningCurveOid);

      if (!learningCurve) {
        return null; // Skip if learning curve not found
      }

      // Find all classes for this learning curve (from all data, not just filtered)
      const classes = this.allRowData
        .filter(row => row.type === 'class' && row.dataPath && row.dataPath[0] === learningCurveOid)
        .map(classRow => ({
          oid: classRow.oid,
          muOid: classRow.muOid,
          fteCount: classRow.fteCount,
          startDate: classRow.startingDate,
          endDate: classRow.endingDate
        }));

      return {
        learningCurveId: learningCurveOid,
        classes: classes
      };
    }).filter(item => item !== null); // Remove null entries

    const requestData = {
      newHireLearningList: newHireLearningList
    };

    // Dispatch the action to apply changes
    this.store.dispatch(new ApplyNewHireClasses({
      planId: this.planDetails.planId,
      data: requestData
    }));
  }

  public noRowsComponentParams = {
    getOverlayMsg: () => {
      let msgKey;
      if(this.allRowData.length===0){
        msgKey = "no.learning.curve.defined.msg";
      }else{
        msgKey = "plan.details.no.mus";
      }
      return this.translate.instant(msgKey);
    },
    image:'1'
  };

  isFullWidthRow(params){
    return params.rowNode.data?.type === 'curve';
  }

  onDeleteClassRow(rowData: any) {
    this._isPristine = false;
    // Only delete class rows
    if (rowData.type !== 'class') return;

    // Get the learning curve OID from the class data path
    if (rowData.dataPath && rowData.dataPath.length > 0) {
      const learningCurveOid = rowData.dataPath[0];
      this.modifiedLearningCurves.add(learningCurveOid);
    }

    this.allRowData = this.allRowData.filter(row => row !== rowData);

    // Apply filtering to update visible data
    this.filterGridBySelectedMUs();
  }

  saveLearningCurveSuccessHandler(data: any) {
    let msg = this.isEditingLearningCurve
      ? this.translate.instant("learning.curve.edited.success.msg")
      : this.translate.instant("learning.curve.added.success.msg");
    this.messageService.add({severity: "success", detail: msg});
    this.isEditingLearningCurve = false;
  }

  saveLearningCurveFailureHandler(error: any) {
    let msg = this.isEditingLearningCurve
      ? this.translate.instant("learning.curve.edit.failed.msg")
      : this.translate.instant("learning.curve.added.failed.msg");
    this.messageService.add({severity: "error", detail: msg});
    this.isEditingLearningCurve = false;
  }

  onEditLearningCurve(rowData: any) {
    // Only edit curve rows
    if (rowData.type !== 'curve') return;
    this.showEditLearningCurveDlg(rowData);
  }

  private hasClassesForLearningCurve(learningCurveOid: string): boolean {
    return this.allRowData.filter(row => row.type === 'class' && row.dataPath && row.dataPath[0] === learningCurveOid).length > 0;
  }

  applyNewHireClassesSuccessHandler(data: any) {
    this.messageService.add({
      severity: "success",
      detail: this.translate.instant("new.hire.classes.applied.success.msg")
    });

    // Clear the modified learning curves set as changes have been applied
    this.modifiedLearningCurves.clear();
    this._isPristine = true;
    this.planDetailsSaved = false;
  }

  applyNewHireClassesFailureHandler(error: any) {
    this.messageService.add({
      severity: "error",
      detail: this.translate.instant("new.hire.classes.apply.failed.msg")
    });
  }

  discardNewHireClassesSuccessHandler(data: NewHireLearningCurvesResponse) {
    this.modifiedLearningCurves.clear();
    this.getNewHireClassesSuccess(data);
  }

  discardNewHireClassesFailedHandler(error: any) {
    this._isLoading = false;
    this.messageService.add({severity: "error", detail: this.translate.instant("new.hire.discard.failure.msg")});
  }

  deleteLearningCurveSuccessHandler(data: any) {
    this.messageService.add({
      severity: "success",
      detail: this.translate.instant("learning.curve.delete.success.msg")
    });
  }

  deleteLearningCurveFailedHandler(error: any) {
    this.messageService.add({
      severity: "error",
      detail: this.translate.instant("learning.curve.delete.failed.msg")
    });
  }

  async canDeactivate(component: CanComponentDeactivate, currentRoute: ActivatedRouteSnapshot, currentState: RouterStateSnapshot,
                      nextState: RouterStateSnapshot) {
    if(this._isLoadingError){
      return true;
    }
    // let plan details parent page handle prompting for unsaved changes in place of unapplied changes (if any)
    if (this.isLeavingPlanDetailsWithUnsavedChanges(nextState)) {
      return true;
    }

    if (this._isPristine) {
      return true;
    }

    // Unapplied changes dialog setup
    const dgOption: DialogOptions = new DialogOptions();
    dgOption.titleKey = "plan.exit.unapplied.dialog.title";
    dgOption.messageKey = "plan.exit.unapplied.dialog.msg";
    dgOption.msgType = "warn";
    dgOption.showCancel = true;
    dgOption.confirmLabel = "btn.confirm.label";

    try {
      await modalComponent.showModalMessage(dgOption, this.modalService);
      return true;
    } catch(error) {
      console.debug("user chose not to leave new hire view");
      return false;
    }
  }

  private isLeavingPlanDetailsWithUnsavedChanges(nextState: RouterStateSnapshot): boolean {
    if (this.planDetailsSaved === false && !nextState?.url?.includes("site-plans")) {
      return true;
    }

    return false;
  }

  @HostListener("window:beforeunload", ["$event"])
  canCloseTab($event: any) {
      if (!this._isPristine) {
          $event.returnValue = true;
      }
  }

  onBtnExport(): void {
    const exParam: any = {
      processHeaderCallback: this.getHeaderForCsv,
      columnGroups: false,
      allColumns: false,
      skipRowGroups: true,
      fileName: this.generateCsvFileName(),
      columnSeparator: ",",
      customHeader: this.getCsvHeader(),
      processCellCallback: this.processCell,
      shouldRowBeSkipped: this.shouldSkipRow,
      columnKeys: ['learningCurveName', 'muName', 'fteCount', 'startingDate', 'endingDate']
    };
    this.gridApi.exportDataAsCsv(exParam);
  }

  private getCsvHeader(): string {
    const dateTime = new Date(Date.now()).toISOString();
    const exportedBy = this.translate.instant("plan.csv.exported.by");
    const exportDate = this.translate.instant("plan.csv.export.date");
    return `"${this.planName}",${exportedBy}: ${this.username},${exportDate}: ${dateTime}`;
  }

  generateCsvFileName(): string {
    return `${this.planName} - NHTC`;
  }

  getHeaderForCsv = (p: any): string => {
    const colId = p.column.colId;

    if (colId === 'learningCurveName') {
      return this.translate.instant('new.hire.learning.curve.label');
    } else if (colId === 'muName') {
      return this.translate.instant('label.mu.short');
    } else if (colId === 'fteCount') {
      return this.translate.instant('new.hire.fte.count');
    } else if (colId === 'startingDate') {
      return this.translate.instant('new.hire.starting.date');
    } else if (colId === 'endingDate') {
      return this.translate.instant('new.hire.ending.date');
    }

    return p.column.colDef.headerName || '';
  };

  processCell = (p: any): string => {
    const colId = p.column.colId;
    const rowData = p.node.data;

    if (!rowData) {
      return '';
    }

    if (colId === 'learningCurveName') {
      if (rowData.type === 'curve') {
        const weeksLabel = this.translate.instant('plan.settings.weeks');
        return rowData.name ? `${rowData.name} (${rowData.trainingWeeks} ${weeksLabel})` : '';
      } else if (rowData.type === 'class') {
        const learningCurveOid = rowData.dataPath?.[0];
        const learningCurve = this.allRowData.find(r => r.type === 'curve' && r.learningCurveId === learningCurveOid);
        if (learningCurve) {
          const weeksLabel = this.translate.instant('plan.settings.weeks');
          return `${learningCurve.name} (${learningCurve.trainingWeeks} ${weeksLabel})`;
        }
        return '';
      }
    }

    if (colId === 'muName') {
      if (rowData.type === 'class') {
        const muItem = this.managementUnitsList?.find(mu => mu.value.oid === rowData.muOid);
        return muItem ? muItem.value.label : rowData.managementUnit;
      }
      return '';
    }

    if (colId === 'startingDate' || colId === 'endingDate') {
      return this.formatDate(p.value);
    }

    if (colId === 'fteCount') {
      return p.value !== null && p.value !== undefined ? p.value.toString() : '';
    }

    return p.value || '';
  };

  shouldSkipRow = (p: any): boolean => {
    return p.node.data?.type === 'curve';
  };
}
